import { useFormatter, useTranslations } from 'next-intl';
import { cx } from '@/styled-system/css';
import { forwardRef } from 'react';
import { getGradeRange, getSchoolTypeLabel, type NearbySchool } from './schoolUtils';
import {
  link,
  ratingBadge,
  ratingBadgeMuted,
  schoolBody,
  schoolDistance,
  schoolItem,
  schoolItemFocusTarget,
  schoolMeta,
  schoolName,
  srOnly,
} from './nearbySchoolsModule.styles';

type SchoolListItemProps = {
  school: NearbySchool;
};

export const SchoolListItem = forwardRef<HTMLLIElement, SchoolListItemProps>(({ school }, ref) => {
  const t = useTranslations('hdp');
  const format = useFormatter();
  const typeLabel = t(
    ({ Public: 'schoolTypePublic', Private: 'schoolTypePrivate', College: 'schoolTypeCollege' } as const)[
      getSchoolTypeLabel(school)
    ],
  );
  const grades = getGradeRange(school.lowGrade, school.highGrade);
  const gradeLabel = grades ? t('schoolGrades', grades) : null;
  const isUniversity = school.type === 'university';
  const rating = school.rating;
  const hasRating = typeof rating === 'number' && rating > 0;
  const ratingLabel = hasRating ? t('schoolRating', { rating }) : t('schoolUnrated');
  const distance = format.number(school.distance);
  const distanceLabel = t('schoolDistance', { distance });

  return (
    <li className={`${schoolItem} ${schoolItemFocusTarget}`} ref={ref} tabIndex={-1}>
      {isUniversity ? (
        <span aria-hidden="true" />
      ) : (
        <span role="img" aria-label={ratingLabel} className={cx(ratingBadge, !hasRating && ratingBadgeMuted)}>
          {hasRating ? school.rating : '-'}
        </span>
      )}
      <div className={schoolBody}>
        <h3 className={schoolName}>
          <a href={school.uriV2} className={link}>
            {school.name}
          </a>
        </h3>
        <p className={schoolMeta}>
          <span>{typeLabel}</span>
          {gradeLabel && (
            <>
              <span aria-hidden="true">{' · '}</span>
              <span className={srOnly}>, </span>
              <span>{gradeLabel}</span>
            </>
          )}
        </p>
      </div>
      <span className={schoolDistance} aria-label={distanceLabel}>
        {distance} mi
      </span>
    </li>
  );
});

SchoolListItem.displayName = 'SchoolListItem';
